/**
 * SortableArea — drag-to-reorder layer for department pages, account-persisted.
 *
 * Wrap a page's cards in <SortableArea pageKey="...">. Each direct child is one
 * movable card; it carries a stable id (the `id` prop — e.g. the <Hideable id>
 * it already uses) and an optional `span` (1–12 columns, default 12 = full row)
 * so the original side-by-side widths survive. In Customize mode (the existing
 * CustomizeBar toggle, via useHide) every card becomes individually draggable;
 * the order saves to the user's account (useUiPref) and follows them across
 * devices. Hiding stays the child's own job (the <Hideable> ✕ + inline "+ Show"
 * stub), so this only adds REORDER.
 */

import React, { useState } from "react";
import { useHide } from "./hide.js";
import { useUiPref } from "./ui-prefs.js";
import { useViewportWidth, BREAK } from "./use-viewport.js";

function meta(el: React.ReactElement, i: number): { id: string; span: number } {
  const p = el.props as { id?: string; "data-card-id"?: string; span?: number; "data-span"?: number };
  const id = String(p.id ?? p["data-card-id"] ?? el.key ?? i);
  const span = Math.max(1, Math.min(12, Number(p.span ?? p["data-span"] ?? 12)));
  return { id, span };
}

export function SortableArea({ pageKey, children, gap = 16 }: { pageKey: string; children: React.ReactNode; gap?: number }): React.ReactElement {
  const h = useHide();
  const editing = h?.editing ?? false;
  const items = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement[];
  const entries = items.map((el, i) => ({ el, ...meta(el, i) }));
  const ids = entries.map((e) => e.id);
  const byId = new Map(entries.map((e) => [e.id, e]));
  const [order, setOrder] = useUiPref<string[]>(`cardorder-${pageKey}`, ids);
  const ordered = [...order.filter((id) => ids.includes(id)), ...ids.filter((id) => !order.includes(id))];

  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const reorder = (targetId: string): void => {
    if (!dragId || dragId === targetId) return;
    const from = ordered.indexOf(dragId);
    const to = ordered.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = ordered.filter((id) => id !== dragId);
    const at = next.indexOf(targetId);
    // Dragging down drops AFTER the target, up drops BEFORE — lands where released.
    next.splice(from < to ? at + 1 : at, 0, dragId);
    setOrder(next);
  };

  // Narrow windows stack every card full-width — a span-4 tile squeezed into a
  // third of a phone screen is unreadable (founder 2026-07-05 responsiveness).
  const oneCol = useViewportWidth() < BREAK.grid;

  return (
    <div style={{ display: "grid", gridTemplateColumns: oneCol ? "minmax(0, 1fr)" : "repeat(12, minmax(0, 1fr))", gap, alignItems: "start" }}>
      {ordered.map((id) => {
        const entry = byId.get(id);
        if (!entry) return null;
        return (
          <div
            key={id}
            draggable={editing}
            onDragStart={editing ? () => setDragId(id) : undefined}
            onDragEnd={editing ? () => { setDragId(null); setOverId(null); } : undefined}
            onDragOver={editing ? (e) => { if (dragId) { e.preventDefault(); setOverId(id); } } : undefined}
            onDrop={editing ? () => { reorder(id); setDragId(null); setOverId(null); } : undefined}
            style={{
              // Let wide tables scroll inside their card instead of forcing
              // the whole page wider than the viewport.
              minWidth: 0,
              gridColumn: oneCol ? "auto" : `span ${entry.span}`,
              ...(editing ? {
                position: "relative",
                cursor: "grab",
                opacity: dragId === id ? 0.4 : 1,
                outline: overId === id && dragId !== id ? "2px solid var(--accent)" : "1px dashed var(--border)",
                outlineOffset: 4,
                borderRadius: "var(--r-card)",
              } : {}),
            }}
          >
            {entry.el}
          </div>
        );
      })}
    </div>
  );
}
